/**
 * The time axis every timeline-family view draws on (`specs/M4-V3-gantt.md` §4.2).
 *
 * monday's Gantt, Timeline and Workload are one renderer `[cap:views/INDEX.md]`, and the thing
 * they share is this: the axis is **day-proportional, not a grid of fixed columns**. Measured on
 * the production Gantt at Quarters zoom, the background cells run 180 · 182 · 184 · 184 px for
 * quarters of 90 · 91 · 92 · 92 days — exactly 2.0 px/day — so a scale is one number and every
 * boundary, bar and drag target derives from it. A 3-day task at Months zoom is therefore 3 px
 * wide instead of snapping to a month, which is what `prod-gantt-deps` actually renders.
 *
 * Pure and dependency-free: no `Date` arithmetic in the browser's offset, only the day index
 * `calendar/events.ts` already established, so a DST boundary cannot move a bar a day.
 */
import { addDays, dayFromIndex, dayIndex } from './calendar/events.js';
/** monday's own order in the 100 × 187 zoom dropdown (`[V3§2.3]`). */
export const TIMELINE_ZOOMS = ['days', 'weeks', 'months', 'quarters', 'years'];
/**
 * Pixels per day per scale.
 *
 * Four of the five are measured on monday itself: **days = 322** (the day pitch on
 * `prod-timeline`), **months = 4.0** (the 111/124/123/122 px run for February–May on the sandbox
 * Gantt after *Auto Fit*), **quarters = 2.0** (the 180/182/184/184 run on `prod-gantt`) and
 * **years = 1.0** (the year labels on `prod-gantt` sit at x 250 · 615 · 979 · 1346 · 1710 — a
 * 364–367 px pitch for a 365-day year). `weeks` is 322 / 7 = 46, which gives a week cell the
 * same 322 px a day cell gets at Days zoom; it is the only one not photographed.
 *
 * Every scale is ≥ 1 px/day, which is what makes `dayAt(x(d)) === d` exact at all five.
 */
export const ZOOM_PX_PER_DAY = {
    days: 322,
    weeks: 46,
    months: 4,
    quarters: 2,
    years: 1,
};
export const ZOOM_MINOR_UNIT = {
    days: 'day',
    weeks: 'week',
    months: 'month',
    quarters: 'quarter',
    years: 'quarter',
};
/** The major (top) row: `days` groups into months, `years` groups quarters into years. */
export const ZOOM_MAJOR_UNIT = {
    days: 'month',
    weeks: 'month',
    months: 'quarter',
    quarters: 'year',
    years: 'year',
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** 1970-01-01 was a Thursday, so `(index + 4) % 7` is the weekday with Sunday = 0. */
export function weekdayOf(day) {
    return (((dayIndex(day) + 4) % 7) + 7) % 7;
}
export function isWeekend(day) {
    const weekday = weekdayOf(day);
    return weekday === 0 || weekday === 6;
}
/** The day name monday prints above the number at Days zoom (`.day-name-label`). */
export function dayNameOf(day) {
    return DAY_NAMES[weekdayOf(day)];
}
const yearOf = (day) => Number(day.slice(0, 4));
const monthOf = (day) => Number(day.slice(5, 7));
function startOfMonth(day) {
    return `${day.slice(0, 7)}-01`;
}
function startOfQuarter(day) {
    const quarter = Math.floor((monthOf(day) - 1) / 3);
    return `${day.slice(0, 4)}-${String(quarter * 3 + 1).padStart(2, '0')}-01`;
}
function startOfYear(day) {
    return `${day.slice(0, 4)}-01-01`;
}
/** Monday-first, which is what the account renders (`[V4§2]`). */
function startOfWeek(day, weekStartsOn = 1) {
    const delta = (weekdayOf(day) - weekStartsOn + 7) % 7;
    return addDays(day, -delta);
}
function nextUnit(day, unit, weekStartsOn = 1) {
    switch (unit) {
        case 'day': return addDays(day, 1);
        case 'week': return addDays(startOfWeek(day, weekStartsOn), 7);
        case 'month': {
            const year = yearOf(day);
            const month = monthOf(day);
            return month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
        }
        case 'quarter': {
            const start = startOfQuarter(day);
            const month = monthOf(start);
            return month === 10 ? `${yearOf(start) + 1}-01-01` : `${yearOf(start)}-${String(month + 3).padStart(2, '0')}-01`;
        }
        case 'year': return `${yearOf(day) + 1}-01-01`;
    }
}
function startOfUnit(day, unit, weekStartsOn = 1) {
    switch (unit) {
        case 'day': return day;
        case 'week': return startOfWeek(day, weekStartsOn);
        case 'month': return startOfMonth(day);
        case 'quarter': return startOfQuarter(day);
        case 'year': return startOfYear(day);
    }
}
function labelOf(day, unit, zoom, major) {
    switch (unit) {
        case 'day': return day.slice(8, 10);
        case 'week': {
            const end = addDays(day, 6);
            return `${MONTHS_SHORT[monthOf(day) - 1]} ${Number(day.slice(8, 10))} – ${Number(end.slice(8, 10))}`;
        }
        // Measured: the minor month cell prints the full name ("February"), the major one the
        // quarter-and-year pair ("Q1 2026") `[cap:prod-gantt.json]`.
        case 'month': return major ? `${MONTHS_SHORT[monthOf(day) - 1]} ${yearOf(day)}` : MONTHS[monthOf(day) - 1];
        case 'quarter': return zoom === 'years' || !major ? `Q${Math.floor((monthOf(day) - 1) / 3) + 1}` : `Q${Math.floor((monthOf(day) - 1) / 3) + 1} ${yearOf(day)}`;
        case 'year': return String(yearOf(day));
    }
}
export function createTimelineAxis(input) {
    const zoom = input.zoom;
    const pxPerDay = input.pxPerDay ?? ZOOM_PX_PER_DAY[zoom];
    const weekStartsOn = input.weekStartsOn ?? 1;
    const majorUnit = ZOOM_MAJOR_UNIT[zoom];
    const minorUnit = ZOOM_MINOR_UNIT[zoom];
    // Snap outward so the header never shows a half cell, which is what monday does.
    const from = startOfUnit(startOfUnit(input.from, minorUnit, weekStartsOn), majorUnit, weekStartsOn);
    const rawEnd = input.to >= input.from ? input.to : input.from;
    const to = addDays(nextUnit(rawEnd, majorUnit, weekStartsOn), -1);
    const first = dayIndex(from);
    const totalDays = dayIndex(to) - first + 1;
    const width = Math.round(totalDays * pxPerDay);
    const x = (day) => Math.floor((dayIndex(day) - first) * pxPerDay);
    const dayAt = (px) => {
        const offset = Math.floor(px / pxPerDay);
        const clamped = Math.min(Math.max(offset, 0), totalDays - 1);
        return dayFromIndex(first + clamped);
    };
    const cells = (unit, major) => {
        const out = [];
        let cursor = startOfUnit(from, unit, weekStartsOn);
        // A snapped-outward start can precede `from` (a week straddling the boundary); clamp the box.
        while (cursor <= to) {
            const next = nextUnit(cursor, unit, weekStartsOn);
            const last = addDays(next, -1);
            const left = Math.max(x(cursor), 0);
            const right = Math.min(x(next), width);
            out.push({
                key: cursor,
                label: labelOf(cursor, unit, zoom, major),
                from: cursor,
                to: last > to ? to : last,
                x: left,
                width: Math.max(right - left, 0),
                weekend: unit === 'day' && isWeekend(cursor),
            });
            cursor = next;
        }
        return out;
    };
    let majorCells = null;
    let minorCells = null;
    return {
        zoom,
        pxPerDay,
        from,
        to,
        width,
        x,
        dayAt,
        bar(barFrom, barTo) {
            const left = x(barFrom);
            const end = barTo >= barFrom ? barTo : barFrom;
            // Inclusive end: a one-day bar spans the whole day, so the right edge is the next day's.
            const right = x(addDays(end, 1));
            return { x: left, width: Math.max(2, right - left) };
        },
        major: () => (majorCells ??= cells(majorUnit, true)),
        minor: () => (minorCells ??= cells(minorUnit, false)),
    };
}
/**
 * *Auto Fit* (`[V3§2.3]`): the most zoomed-**in** scale whose data span still fits the viewport.
 * Measured on the sandbox, Auto Fit moved Quarters → Months for a 41-day data span in a ~1,010 px
 * body and then rendered a two-year range around it — so the scale is chosen from the **data**
 * and the padding is the renderer's, not this function's.
 */
export function fitZoom(span, viewportWidth) {
    // `span` is the **data's** extent, not the padded range: after Auto Fit the sandbox Gantt sat
    // at Months with a 41-day data span in a ~1,010 px body while its rendered range was two years.
    const days = Math.max(1, dayIndex(span.to) - dayIndex(span.from) + 1);
    const budget = Math.max(1, viewportWidth);
    for (const zoom of TIMELINE_ZOOMS) {
        if (days * ZOOM_PX_PER_DAY[zoom] <= budget)
            return zoom;
    }
    return 'years';
}
/** The data's own extent, for the initial range. `null` when nothing is dated. */
export function spanOfDays(days) {
    let from = null;
    let to = null;
    for (const day of days) {
        if (from === null || day < from)
            from = day;
        if (to === null || day > to)
            to = day;
    }
    return from !== null && to !== null ? { from, to } : null;
}
//# sourceMappingURL=timeline-axis.js.map